import React from 'react';
import './DocBlocks.css';

// Page wrapper with TOC
export function DocPage({ children, toc }) {
  return (
    <div className="doc-page">
      <div className="doc-body">
        {children}
        <div className="doc-page-end" aria-hidden="true" />
      </div>
      {toc && toc.length > 0 && (
        <aside className="doc-toc">
          <span className="doc-toc-label">On this page</span>
          <ul className="doc-toc-list">
            {toc.map((item) => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  className={`doc-toc-item${item.indent ? ' doc-toc-item--indent' : ''}`}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </aside>
      )}
    </div>
  );
}

// Page header
export function PageHeader({ eyebrow, title, description, badge }) {
  return (
    <div className="page-header">
      {eyebrow && <span className="page-eyebrow">{eyebrow}</span>}
      <h1 className="page-title">
        {title}
        {badge && <span className={`page-badge page-badge--${badge.variant}`}>{badge.label}</span>}
      </h1>
      {description && <p className="page-description">{description}</p>}
      <div className="page-divider" />
    </div>
  );
}

// Section heading (h2)
export function Section({ id, title, children }) {
  return (
    <section className="doc-section" id={id}>
      <h2 className="section-title">
        {title}
        <a href={`#${id}`} className="section-anchor" aria-label={`Link to ${title}`}>#</a>
      </h2>
      {children}
    </section>
  );
}

// Sub-section heading (h3)
export function SubSection({ id, title, children }) {
  return (
    <div className="doc-subsection" id={id}>
      <h3 className="subsection-title">
        {title}
        <a href={`#${id}`} className="section-anchor section-anchor--sub" aria-label={`Link to ${title}`}>#</a>
      </h3>
      {children}
    </div>
  );
}

// Regular paragraph
export function Para({ children }) {
  return <p className="doc-para">{children}</p>;
}

// Callout box
export function Callout({ type = 'info', title, children }) {
  return (
    <div className={`callout callout--${type}`}>
      <div className="callout-icon">{calloutIcon(type)}</div>
      <div className="callout-content">
        {title && <strong className="callout-title">{title}</strong>}
        <p className="callout-body">{children}</p>
      </div>
    </div>
  );
}

function calloutIcon(type) {
  const icons = { info: 'ℹ', tip: '✦', warning: '⚠', danger: '✕' };
  return icons[type] || 'ℹ';
}

// Code block
export function Code({ language = 'plaintext', children }) {
  return (
    <div className="code-block">
      {language && <span className="code-lang">{language}</span>}
      <pre className="code-pre"><code>{children}</code></pre>
    </div>
  );
}

// Inline code
export function InlineCode({ children }) {
  return <code className="inline-code">{children}</code>;
}

// Definition list / key-value grid
export function DefList({ items }) {
  return (
    <dl className="def-list">
      {items.map((item, i) => (
        <div key={i} className="def-item">
          <dt className="def-term">
            <InlineCode>{item.term}</InlineCode>
            {item.type && <span className="def-type">{item.type}</span>}
          </dt>
          <dd className="def-desc">{item.desc}</dd>
        </div>
      ))}
    </dl>
  );
}

// Card grid
export function CardGrid({ children }) {
  return <div className="card-grid">{children}</div>;
}

export function Card({ icon, title, description, accent }) {
  return (
    <div className={`info-card${accent ? ` info-card--${accent}` : ''}`}>
      {icon && <span className="info-card-icon">{icon}</span>}
      <strong className="info-card-title">{title}</strong>
      {description && <p className="info-card-desc">{description}</p>}
    </div>
  );
}

// Step list
export function Steps({ items }) {
  return (
    <ol className="steps-list">
      {items.map((item, i) => (
        <li key={i} className="step-item">
          <span className="step-num">{i + 1}</span>
          <div className="step-content">
            {item.title && <strong className="step-title">{item.title}</strong>}
            <p className="step-desc">{item.desc}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

// Table
export function DocTable({ headers, rows }) {
  return (
    <div className="table-wrap">
      <table className="doc-table">
        <thead>
          <tr>
            {headers.map((h, i) => <th key={i}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => <td key={j}>{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Prerequisites — shown near top of page to tell users what to read first
export function Prerequisites({ items, onNavigate }) {
  return (
    <div className="prereq">
      <span className="prereq-label">Before this</span>
      <div className="prereq-items">
        {items.map((item) => (
          <button key={item.id} className="prereq-item" onClick={() => onNavigate?.(item.id)}>
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// NextStep — shown at bottom of page to guide the reader forward
export function NextStep({ items, onNavigate }) {
  return (
    <div className="next-step">
      <span className="next-step-label">Read next</span>
      <div className="next-step-items">
        {items.map((item) => (
          <button key={item.id} className="next-step-card" onClick={() => onNavigate?.(item.id)}>
            <span className="next-step-title">{item.title}</span>
            <span className="next-step-desc">{item.desc}</span>
            <span className="next-step-arrow">→</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// Tooltip — inline term with dotted underline and hover explanation box
export function Tooltip({ children, tip }) {
  return (
    <span className="doc-tooltip">
      <span className="doc-tooltip-term">{children}</span>
      <span className="doc-tooltip-box">{tip}</span>
    </span>
  );
}

// Placeholder (for sections not yet documented)
export function Placeholder({ label = 'Coming soon' }) {
  return (
    <div className="placeholder">
      <span className="placeholder-icon">✦</span>
      <span className="placeholder-text">{label}</span>
    </div>
  );
}
